import { useState, useMemo, useCallback } from 'react';
import {
  Avatar, Button, Card, IconButton, InputAdornment, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField as MuiTextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import userApi from '../../services/userApi';
import InviteLenderAdminModal from './components/InviteLenderAdminModal';
import { useToast } from '../../components/shared/Toast';
import { useConfirmModal } from '../../components/shared/ConfirmModal';
import StatusBadge, { USER_STATUS_MAP, ROLE_MAP } from '../../components/shared/StatusBadge';
import PageHeader from '../../components/shared/PageHeader';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';

const LenderUsersPage = () => {
  const { data: users = [], isLoading } = userApi.endpoints.getLenderUsers.useQuery();
  const [deleteLenderUser] = userApi.endpoints.deleteLenderUser.useMutation();
  const [resendConfirmation] = userApi.endpoints.resendLenderUserConfirmation.useMutation();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const { showToast, ToastContainer } = useToast();
  const { confirm, ConfirmModalContainer } = useConfirmModal();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = useCallback((column) => {
    setSortDirection(prev => sortColumn === column ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortColumn(column);
  }, [sortColumn]);

  const handleDelete = async (user) => {
    const confirmed = await confirm({
      title: 'Remove User',
      message: `Are you sure you want to remove ${user.fullName}?`,
      confirmText: 'Remove',
    });
    if (!confirmed) return;
    try {
      await deleteLenderUser({ userId: user.userId }).unwrap();
    } catch {
      // error handled by RTK Query
    }
  };

  const handleResend = async (user) => {
    try {
      await resendConfirmation({ userId: user.userId }).unwrap();
      showToast(`Confirmation email resent to ${user.emailAddress}.`);
    } catch {
      showToast('Failed to resend confirmation email.', 'danger');
    }
  };

  const getUserStatus = (user) => {
    if (user.inactive) return 'Inactive';
    if (user.isPendingApproval) return 'PendingApproval';
    if (!user.confirmed) return 'PendingConfirmation';
    return 'Active';
  };

  const processedUsers = useMemo(() => {
    let list = [...users];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(u =>
        u.fullName?.toLowerCase().includes(term) ||
        u.emailAddress?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term)
      );
    }
    list.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'name': aVal = (a.fullName || '').toLowerCase(); bVal = (b.fullName || '').toLowerCase(); break;
        case 'email': aVal = (a.emailAddress || '').toLowerCase(); bVal = (b.emailAddress || '').toLowerCase(); break;
        case 'role': aVal = a.role || ''; bVal = b.role || ''; break;
        case 'status': aVal = getUserStatus(a); bVal = getUserStatus(b); break;
        default: aVal = ''; bVal = '';
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [users, searchTerm, sortColumn, sortDirection]);

  if (isLoading) {
    return <LoadingSkeleton variant="page" />;
  }

  return (
    <>
      {ToastContainer}
      {ConfirmModalContainer}

      <PageHeader
        title="Users"
        subtitle="Manage lender administrators and staff"
        actions={
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setShowInviteModal(true)}>
            Invite User
          </Button>
        }
      />

      <Card>
        {users.length === 0 ? (
          <EmptyState
            icon={<GroupIcon />}
            title="No lender users yet"
            description="Invite a Lender Admin to get started."
          />
        ) : (
          <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
            <MuiTextField
              size="small" placeholder="Search users..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
              {processedUsers.length} of {users.length} users
            </Typography>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortColumn === 'name' ? sortDirection : false}>
                    <TableSortLabel active={sortColumn === 'name'} direction={sortColumn === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>Name</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortColumn === 'email' ? sortDirection : false}>
                    <TableSortLabel active={sortColumn === 'email'} direction={sortColumn === 'email' ? sortDirection : 'asc'} onClick={() => handleSort('email')}>Email</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortColumn === 'role' ? sortDirection : false}>
                    <TableSortLabel active={sortColumn === 'role'} direction={sortColumn === 'role' ? sortDirection : 'asc'} onClick={() => handleSort('role')}>Role</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortColumn === 'status' ? sortDirection : false}>
                    <TableSortLabel active={sortColumn === 'status'} direction={sortColumn === 'status' ? sortDirection : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No users match your search</Typography>
                    </TableCell>
                  </TableRow>
                ) : processedUsers.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                          {user.fullName?.split(' ').map(n => n[0]).join('') || '?'}
                        </Avatar>
                        <Typography variant="body2">{user.fullName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Typography variant="body2">{user.emailAddress}</Typography></TableCell>
                    <TableCell><StatusBadge status={user.role} map={ROLE_MAP} /></TableCell>
                    <TableCell><StatusBadge status={getUserStatus(user)} map={USER_STATUS_MAP} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {!user.confirmed && !user.isPendingApproval && (
                          <Tooltip title="Resend confirmation email">
                            <IconButton size="small" color="warning" onClick={() => handleResend(user)}>
                              <ForwardToInboxIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove user">
                          <IconButton size="small" color="error" onClick={() => handleDelete(user)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </Card>

      {showInviteModal && (
        <InviteLenderAdminModal onClose={() => setShowInviteModal(false)} />
      )}
    </>
  );
};

export default LenderUsersPage;
